// TTS voice/language data model.
//
// Catalog v4 (voxist-tts) made stock voices PER-LANGUAGE native speakers — each
// voice belongs to ONE language (its `target_language`) and is described by a
// vocal range (soprano/alto/tenor/…). This replaces the old "one brand voice
// speaks every language + pitch variants" model. Cloned voices are the
// exception: a user's own voice IS cross-language (OmniVoice zero-shot), so it
// carries `languages[]` and language is chosen per-synthesis.

// Matches apirest-v3/src/tts/dto/voice.dto.ts (VoiceDto). The gateway passes the
// upstream catalog response through verbatim, so the shape follows the TTS
// service, not the DTO class. Catalog v4 returns, per voice:
//   { voice_id, name, gender, pitches: string[], languages: string[], featured }
// Older deployments returned `target_language`/`pitch_class`/`labels.*` instead;
// both are tolerated by the adapter (see `mapVoiceDto`).
export interface TtsVoiceRaw {
  voice_id: string
  name: string
  description?: string
  labels?: Record<string, string>
  preview_url?: string
  created_at?: string

  // --- catalog v4 top-level fields ---
  /** Languages this voice can render. Stock voices carry exactly one. */
  languages?: string[]
  /** Vocal ranges/pitch variants this voice can be synthesized at. */
  pitches?: string[]
  gender?: string
  featured?: boolean

  // --- legacy fields (older upstreams) ---
  target_language?: string
  locale?: string
  pitch_class?: string
  range?: string
  pitch?: string
  f0_hz?: number | string
}

/** Vocal range → a short, human timbre descriptor (grounded in the real
 *  `pitch_class`; far more evocative than "moderate/high pitch"). */
export const VOCAL_RANGE_DESC: Record<string, string> = {
  soprano: 'bright, high',
  'mezzo-soprano': 'warm, mid-range',
  alto: 'deep, rich',
  tenor: 'clear, higher',
  baritone: 'deep, resonant',
  bass: 'very deep',
}

/** Frontend voice view. STOCK voices are language-bound; CLONED voices are the
 *  user's own and may render any language (`languages` populated). */
export interface TtsVoice extends TtsVoiceRaw {
  /** Human display name (e.g. "Lena"). Falls back to a range/id label. */
  displayName: string
  /** The voice's native language (stock) or the recording language (cloned). */
  language: string
  /** 'stock' | 'cloned' */
  type: string
  gender: string
  /** Vocal range from `pitch_class` (soprano/alto/tenor/…); '' if unknown. */
  range: string
  /** Fundamental frequency in Hz, when known (drives the pitch indicator). */
  f0?: number
  isCloned: boolean
  /** Every language this voice can render, straight from the catalog. Stock
   *  voices carry exactly one (equal to `language`); cloned/zero-shot voices
   *  legitimately span several. `[]` only when the catalog omits it. */
  languages: string[]
  /** Raw catalog `pitches` — every range this voice can be synthesized at. A
   *  catalog-v4 reference voice carries exactly one (surfaced as `range`); the
   *  ~100 legacy embedding voices report SEVERAL, in their own relative
   *  vocabulary (moderate/high/very high). `range` is only the primary/display
   *  value, so filtering must go through `voiceRanges` — filtering on `range`
   *  alone hides a voice from every range it supports but the first. */
  pitches: string[]

  // --- deprecated (kept so existing consumers still compile during migration) ---
  /** @deprecated use `range`. */
  pitch_class?: string
}

/** A language the TTS service supports. */
export interface TtsLanguage {
  code: string
  name: string
  /** Native endonym for display (e.g. "Deutsch"); falls back to `name`. */
  nativeName?: string
  /** Number of stock voices available for this language. */
  voiceCount?: number
  /** Whether live streaming synthesis is available. The service advertises this
   *  as `true` for EVERY supported language; treat a missing value as `true`. */
  streaming?: boolean
  /** True for the languages served with the lowest latency (en/fr). This is NOT
   *  a streaming capability — every language streams — so it must never be
   *  rendered as a badge implying other languages can't. Surface it only next to
   *  the streaming control, as latency information. The underlying engine is
   *  intentionally never exposed to users. */
  lowLatency?: boolean
}

// Matches apirest-v3/src/tts/dto/synthesize.dto.ts
export type AudioFormat = 'wav' | 'pcm_f32le' | 'pcm_s16le'

export interface TtsSynthesizeRequest {
  text: string               // @MaxLength on backend (buffered path); streamed pcm lifts it
  language?: string          // ISO 639-1, e.g. "en", "fr", "de"
  pitch?: string             // pitch class for variant resolution (legacy voices)
  format?: AudioFormat
  cfg_coef?: number          // 0-10
  temperature?: number       // 0-2
  top_k?: number             // 1-1000
  max_duration_s?: number    // 1-300
}

export interface TtsSynthesizeResponse {
  audioBlob: Blob
  ttfbMs: number
  totalTimeMs: number
  charactersProcessed: number
  requestId?: string         // from X-Request-Id response header
  audioDurationS?: number    // from X-Audio-Duration-S response header
}

// WebSocket message: client sends only { text } -- voiceId is in the URL path
export interface TtsStreamMessage {
  text: string
}

export interface TtsUsageStats {
  totalCharacters: number
  totalCost: number
  thisMonth: { characters: number; cost: number }
  lastMonth: { characters: number; cost: number }
  dailyUsage: Array<{ date: string; characters: number; cost: number }>
}

/** Bucket key for a voice whose language the catalog didn't resolve. */
export const UNKNOWN_LANG = 'unknown'

/**
 * EVERY language bucket a voice belongs to (never empty).
 *
 * `languages` is the contract: a catalog-v4 reference voice is language-bound
 * and carries exactly one entry, but cloned/zero-shot and legacy voices
 * legitimately advertise several. Bucketing on `languages[0]` alone made a voice
 * advertising ['en','fr'] unreachable under French.
 */
export function voiceLanguageBuckets(voice: TtsVoice): string[] {
  const codes = (voice.languages ?? []).filter(Boolean)
  if (codes.length > 0) return Array.from(new Set(codes))
  return [voice.language || UNKNOWN_LANG]
}

/** The voice's PRIMARY language bucket — display and default only. Membership
 *  questions must go through `voiceMatchesLanguage`. */
export function voiceLanguageBucket(voice: TtsVoice): string {
  return voiceLanguageBuckets(voice)[0]
}

/** Whether a voice belongs to a language bucket (any of its `languages`). */
export function voiceMatchesLanguage(voice: TtsVoice, code: string): boolean {
  return voiceLanguageBuckets(voice).includes(code)
}

/** Group stock voices by language → { lang: voices[] }. A multi-language voice
 *  appears under EVERY language it advertises. */
export function groupVoicesByLanguage(voices: TtsVoice[]): Record<string, TtsVoice[]> {
  return voices.reduce<Record<string, TtsVoice[]>>((acc, v) => {
    voiceLanguageBuckets(v).forEach((lang) => {
      ;(acc[lang] ||= []).push(v)
    })
    return acc
  }, {})
}

/** The voices belonging to one language bucket. Shared by the playground and the
 *  gallery so "no voices available for this language" can't disagree with the
 *  language chip's count. */
export function voicesForLanguage(voices: TtsVoice[], code: string): TtsVoice[] {
  return voices.filter((v) => voiceMatchesLanguage(v, code))
}

// Real vocal ranges (catalog v4), highest → lowest. Each voice has exactly one.
const RANGE_ORDER = ['soprano', 'mezzo-soprano', 'alto', 'tenor', 'baritone', 'bass']

// Legacy embedding voices describe pitch RELATIVELY instead, in their own
// vocabulary. The two are never unified or translated — they just coexist in the
// languages that host both (en/fr), with the real ranges always listed first.
const LEGACY_PITCH_ORDER = ['very low', 'low', 'moderate', 'high', 'very high']

const KNOWN_RANGES = [...RANGE_ORDER, ...LEGACY_PITCH_ORDER]

/**
 * EVERY vocal range a voice can be synthesized at: its primary `range` plus the
 * catalog's full `pitches` list, deduped.
 *
 * `range` is a display value (the first pitch, or an explicit legacy
 * `pitch_class`). The ~100 legacy embedding voices carry several pitches — Ada
 * is ['moderate','high','very high'] — and matching on `range` alone hid them
 * from the 'high' chip they genuinely support.
 */
export function voiceRanges(voice: TtsVoice): string[] {
  const all = [voice.range, ...(voice.pitches ?? [])].filter(Boolean)
  return Array.from(new Set(all))
}

/** Whether a voice can be synthesized at a range (checks its FULL pitch list). */
export function voiceMatchesRange(voice: TtsVoice, range: string): boolean {
  return voiceRanges(voice).includes(range)
}

/**
 * The distinct vocal ranges present in a voice set — real ranges in canonical
 * order first, then legacy relative pitches, then anything unrecognized.
 *
 * Derived from the voices' FULL pitch lists, so a range chip is offered exactly
 * when some voice supports it — and every offered chip yields that voice.
 * Availability genuinely varies by language (es has
 * tenor/baritone/mezzo-soprano/soprano, hr only three of those).
 */
export function availableRanges(voices: TtsVoice[]): string[] {
  const present = new Set<string>()
  voices.forEach((v) => voiceRanges(v).forEach((r) => present.add(r)))
  return KNOWN_RANGES.filter((r) => present.has(r)).concat(
    Array.from(present).filter((r) => !KNOWN_RANGES.includes(r))
  )
}

const GENDER_ORDER = ['female', 'male']

/** The genders present in a voice set, in stable order. Same rule as ranges: a
 *  gender chip is only ever offered when some voice actually has it. */
export function availableGenders(voices: TtsVoice[]): string[] {
  const present = new Set(voices.map((v) => v.gender))
  return GENDER_ORDER.filter((g) => present.has(g))
}

/** Active gender/range narrowing. `gender: 'all'` and `range: ''` mean "no filter". */
export interface VoiceFilterCriteria {
  gender?: string
  range?: string
}

/**
 * The voices left after the gender/range narrowing — the single definition of
 * "visible", shared by the playground (which renders it and reconciles the
 * selection to it) and by the chip-availability logic (which asks what a
 * candidate filter WOULD leave).
 */
export function filterVoices(
  voices: TtsVoice[],
  { gender = 'all', range = '' }: VoiceFilterCriteria
): TtsVoice[] {
  return voices.filter((v) => {
    if (gender !== 'all' && v.gender !== gender) return false
    if (range && !voiceMatchesRange(v, range)) return false
    return true
  })
}

/**
 * Reconcile the selected voice against the VISIBLE list, so a filtered-out voice
 * can never be the one synthesized.
 *
 * Returns the voice_id to select, or `null` to leave the selection untouched.
 * Cloned voices are zero-shot (they speak any language, and never appear in the
 * stock list) so a cloned selection is always valid and never clobbered.
 */
export function reconcileSelectedVoice(
  selectedVoiceId: string,
  allVoices: TtsVoice[],
  visibleVoices: TtsVoice[]
): string | null {
  const current = allVoices.find((v) => v.voice_id === selectedVoiceId)
  if (current?.isCloned) return null
  if (visibleVoices.length === 0) return selectedVoiceId ? '' : null
  if (visibleVoices.some((v) => v.voice_id === selectedVoiceId)) return null
  return visibleVoices[0].voice_id
}

/**
 * The language to send to the synthesis API.
 *
 * The synthetic 'unknown' bucket is NOT a language the service knows, so we must
 * not send it — but sending nothing makes the service apply its own default (en)
 * to a voice that may not be English at all. Fall back to the voice's own
 * language; `undefined` only when the voice genuinely doesn't declare one.
 */
export function resolveSynthesisLanguage(
  selectedLanguage: string,
  voice?: TtsVoice
): string | undefined {
  if (selectedLanguage && selectedLanguage !== UNKNOWN_LANG) return selectedLanguage
  const declared = (voice?.languages ?? []).find((c) => c && c !== UNKNOWN_LANG)
  if (declared) return declared
  if (voice?.language && voice.language !== UNKNOWN_LANG) return voice.language
  return undefined
}
